import {
    Box,
    Stack,
    TextField,
    FormControl,
    Select,
    InputLabel,
    MenuItem,
    FormControlLabel,
    Radio,
    RadioGroup,
    Button,
    Grid,
    CircularProgress,
    Card,
    CardContent,
    Typography,
    Divider,
    useTheme,
    InputAdornment
} from '@mui/material';
import { useEffect } from 'react';
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { store } from '../../../../store';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
    setCurrentScreenLoading,
    setCurrentScreenName,
    setCurrentScreenState,
    setCurrentScreenId
} from '../Slices/currentScreen';
import {
    createQuestion,
    editQuestion,
    getIndividualQuestion
} from '../../../../API/Course';
import QuizIcon from '@mui/icons-material/Quiz';

const getSelectedAnswerKey = (item) => {
    if (!item?.answer) return '';
    if (['option1', 'option2', 'option3', 'option4'].includes(item.answer)) {
        return item.answer;
    }
    const optKey = Object.keys(item?.options || {}).find(
        (key) =>
            item.options[key]?.trim().toLowerCase() ===
            item.answer.trim().toLowerCase()
    );
    return optKey || '';
};

const Exercise = (props) => {
    const loader = useSelector((state) => state?.currentScreen?.isLoading);
    const [formData, setFormData] = useState({
        questionType: 'mcq',
        question: '',
        options: {
            option1: '',
            option2: '',
            option3: '',
            option4: ''
        },
        answer: ''
    });
    const dispatch = useDispatch();
    const shouldEditQuestion = useSelector(
        (state) => state?.currentScreen?.editable
    );
    const questionId = useSelector((state) => state?.currentScreen?.id);
    const sectionId = useSelector(
        (state) => state?.currentCourse?.currentSectionId
    );

    const currentCourse = useSelector((state) => state?.currentCourse?.data);
    const activeSection = currentCourse?.sections?.find(
        (sec) => sec._id === sectionId
    );
    const activeSectionName = activeSection ? activeSection.title : '';

    const theme = useTheme();

    const handleChange = (e) => {
        const { name, value } = e.target;
        const trimmedValue = value.replace(/^\s+/, '');
        const trimmedName = name.replace(/^\s+/, '');

        console.log('value to be changed', name);
        if (trimmedName.slice(0, 6) === 'option') {
            setFormData({
                ...formData,
                options: { ...formData.options, [trimmedName]: trimmedValue }
            });
        } else {
            setFormData({ ...formData, [trimmedName]: trimmedValue });
        }
    };

    const handleSubmit = async () => {
        console.log('Form data in handlesubmit', formData);
        let isEmpty;
        let isOptions;

        isEmpty = Object.values(formData).some((x) => x === null || x === '');
        isOptions = Object.values(formData.options).some(
            (x) => x === null || x === ''
        );

        if (
            isEmpty ||
            !sectionId ||
            (formData.questionType === 'mcq' && isOptions && formData.answer)
        ) {
            console.log('Empty field', isEmpty, isOptions, sectionId);
            toast.error('Please fill all the fields');
        } else {
            if (shouldEditQuestion) {
                await editQuestion(formData, questionId, dispatch);
                toast.success('Question Updated successfully!');
            } else {
                await createQuestion(formData, sectionId, dispatch);
                toast.success('Question Created successfully!');
            }

            dispatch(setCurrentScreenName('manual'));
            dispatch(setCurrentScreenState(false));
            dispatch(setCurrentScreenId(''));

            setFormData({
                questionType: 'mcq',
                question: '',
                options: {
                    option1: '',
                    option2: '',
                    option3: '',
                    option4: ''
                },
                answer: ''
            });
        }
    };

    const getCurrentQuestion = async () => {
        store.dispatch(setCurrentScreenLoading(true));
        await getIndividualQuestion(questionId).then((response) => {
            console.log('all questions fetched', response);

            setFormData({
                questionType: response?.data?.excercise?.questionType,
                question: response?.data?.excercise?.question,
                options: {
                    option1: response?.data?.excercise?.options[0]?.a,
                    option2: response?.data?.excercise?.options[1]?.b,
                    option3: response?.data?.excercise?.options[2]?.c,
                    option4: response?.data?.excercise?.options[3]?.d
                },
                answer: response?.data?.excercise?.answer
            });
            store.dispatch(setCurrentScreenLoading(false));
        });
    };

    const resetQuestions = () =>
        setFormData({
            questionType: 'mcq',
            question: '',
            options: {
                option1: '',
                option2: '',
                option3: '',
                option4: ''
            },
            answer: ''
        });

    useEffect(() => {
        questionId ? getCurrentQuestion() : resetQuestions();
    }, [questionId]);

    return (
        <>
            {loader ? (
                <Box
                    textAlign="center"
                    height="60vh"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                >
                    <CircularProgress />
                </Box>
            ) : (
                <Box>
                    <Card
                        elevation={3}
                        sx={{
                            borderRadius: '16px',
                            border: `1px solid ${theme.palette.divider}`,
                            overflow: 'hidden',
                            mb: 2
                        }}
                    >
                        <Box
                            sx={{
                                p: 3,
                                backgroundColor: theme.palette.primary.main,
                                color: '#fff',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 2
                            }}
                        >
                            <QuizIcon
                                sx={{
                                    fontSize: '2.2rem',
                                    color: theme.palette.secondary.main
                                }}
                            />
                            <Box>
                                <Typography
                                    variant="h5"
                                    sx={{ fontWeight: 700, mb: 0.5 }}
                                >
                                    {shouldEditQuestion
                                        ? 'Edit Exercise Question'
                                        : 'Create Exercise Question'}
                                </Typography>
                                <Typography
                                    variant="body2"
                                    sx={{ opacity: 0.85 }}
                                >
                                    Adding interactive questions under manual:{' '}
                                    <strong>
                                        {activeSectionName ||
                                            'Selected Section'}
                                    </strong>
                                </Typography>
                            </Box>
                        </Box>

                        <Divider />

                        <CardContent sx={{ p: 4 }}>
                            <Stack spacing={4}>
                                <Grid container spacing={3}>
                                    <Grid item xs={12} md={6}>
                                        <FormControl fullWidth>
                                            <InputLabel id="question-type-select-label">
                                                Question Type
                                            </InputLabel>
                                            <Select
                                                labelId="question-type-select-label"
                                                id="question-type-select"
                                                name="questionType"
                                                label="Question Type"
                                                value={formData?.questionType}
                                                onChange={handleChange}
                                                sx={{ borderRadius: '10px' }}
                                            >
                                                <MenuItem value="mcq">
                                                    Multiple Choice Question
                                                    (MCQ)
                                                </MenuItem>
                                                <MenuItem value="true/false">
                                                    True / False
                                                </MenuItem>
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                </Grid>

                                <Box>
                                    <Typography
                                        variant="subtitle2"
                                        sx={{
                                            fontWeight: 600,
                                            color: 'text.secondary',
                                            mb: 1.5
                                        }}
                                    >
                                        Question Statement
                                    </Typography>
                                    <TextField
                                        fullWidth
                                        multiline
                                        rows={3}
                                        label="Question Description"
                                        name="question"
                                        value={formData?.question}
                                        onChange={handleChange}
                                        placeholder="Enter your question statement here..."
                                        InputProps={{
                                            style: {
                                                borderRadius: '12px'
                                            }
                                        }}
                                    />
                                </Box>

                                <Divider />

                                {formData?.questionType === 'mcq' ? (
                                    <Stack spacing={3}>
                                        <Box>
                                            <Typography
                                                variant="subtitle2"
                                                sx={{
                                                    fontWeight: 600,
                                                    color: 'text.secondary',
                                                    mb: 2
                                                }}
                                            >
                                                MCQ Option Settings
                                            </Typography>
                                            <Grid container spacing={2.5}>
                                                <Grid item xs={12} sm={6}>
                                                    <TextField
                                                        fullWidth
                                                        label="Option A"
                                                        name="option1"
                                                        value={
                                                            formData?.options
                                                                ?.option1
                                                        }
                                                        onChange={handleChange}
                                                        InputLabelProps={{
                                                            shrink: true
                                                        }}
                                                        placeholder="Enter option A content"
                                                        InputProps={{
                                                            startAdornment: (
                                                                <InputAdornment position="start">
                                                                    <Box
                                                                        sx={{
                                                                            width: 24,
                                                                            height: 24,
                                                                            borderRadius:
                                                                                '50%',
                                                                            backgroundColor:
                                                                                'rgba(82, 146, 221, 0.1)',
                                                                            color: 'secondary.main',
                                                                            display:
                                                                                'flex',
                                                                            alignItems:
                                                                                'flex-start',
                                                                            justifyContent:
                                                                                'center',
                                                                            fontWeight:
                                                                                'bold',
                                                                            fontSize:
                                                                                '0.85rem'
                                                                        }}
                                                                    >
                                                                        A
                                                                    </Box>
                                                                </InputAdornment>
                                                            ),
                                                            style: {
                                                                borderRadius:
                                                                    '10px'
                                                            }
                                                        }}
                                                    />
                                                </Grid>
                                                <Grid item xs={12} sm={6}>
                                                    <TextField
                                                        fullWidth
                                                        label="Option B"
                                                        name="option2"
                                                        value={
                                                            formData?.options
                                                                ?.option2
                                                        }
                                                        onChange={handleChange}
                                                        InputLabelProps={{
                                                            shrink: true
                                                        }}
                                                        placeholder="Enter option B content"
                                                        InputProps={{
                                                            startAdornment: (
                                                                <InputAdornment position="start">
                                                                    <Box
                                                                        sx={{
                                                                            width: 24,
                                                                            height: 24,
                                                                            borderRadius:
                                                                                '50%',
                                                                            backgroundColor:
                                                                                'rgba(82, 146, 221, 0.1)',
                                                                            color: 'secondary.main',
                                                                            display:
                                                                                'flex',
                                                                            alignItems:
                                                                                'flex-start',
                                                                            justifyContent:
                                                                                'center',
                                                                            fontWeight:
                                                                                'bold',
                                                                            fontSize:
                                                                                '0.85rem'
                                                                        }}
                                                                    >
                                                                        B
                                                                    </Box>
                                                                </InputAdornment>
                                                            ),
                                                            style: {
                                                                borderRadius:
                                                                    '10px'
                                                            }
                                                        }}
                                                    />
                                                </Grid>
                                                <Grid item xs={12} sm={6}>
                                                    <TextField
                                                        fullWidth
                                                        label="Option C"
                                                        name="option3"
                                                        value={
                                                            formData?.options
                                                                ?.option3
                                                        }
                                                        onChange={handleChange}
                                                        InputLabelProps={{
                                                            shrink: true
                                                        }}
                                                        placeholder="Enter option C content"
                                                        InputProps={{
                                                            startAdornment: (
                                                                <InputAdornment position="start">
                                                                    <Box
                                                                        sx={{
                                                                            width: 24,
                                                                            height: 24,
                                                                            borderRadius:
                                                                                '50%',
                                                                            backgroundColor:
                                                                                'rgba(82, 146, 221, 0.1)',
                                                                            color: 'secondary.main',
                                                                            display:
                                                                                'flex',
                                                                            alignItems:
                                                                                'flex-start',
                                                                            justifyContent:
                                                                                'center',
                                                                            fontWeight:
                                                                                'bold',
                                                                            fontSize:
                                                                                '0.85rem'
                                                                        }}
                                                                    >
                                                                        C
                                                                    </Box>
                                                                </InputAdornment>
                                                            ),
                                                            style: {
                                                                borderRadius:
                                                                    '10px'
                                                            }
                                                        }}
                                                    />
                                                </Grid>
                                                <Grid item xs={12} sm={6}>
                                                    <TextField
                                                        fullWidth
                                                        label="Option D"
                                                        name="option4"
                                                        value={
                                                            formData?.options
                                                                ?.option4
                                                        }
                                                        onChange={handleChange}
                                                        InputLabelProps={{
                                                            shrink: true
                                                        }}
                                                        placeholder="Enter option D content"
                                                        InputProps={{
                                                            startAdornment: (
                                                                <InputAdornment position="start">
                                                                    <Box
                                                                        sx={{
                                                                            width: 24,
                                                                            height: 24,
                                                                            borderRadius:
                                                                                '50%',
                                                                            backgroundColor:
                                                                                'rgba(82, 146, 221, 0.1)',
                                                                            color: 'secondary.main',
                                                                            display:
                                                                                'flex',
                                                                            alignItems:
                                                                                'flex-start',
                                                                            justifyContent:
                                                                                'center',
                                                                            fontWeight:
                                                                                'bold',
                                                                            fontSize:
                                                                                '0.85rem'
                                                                        }}
                                                                    >
                                                                        D
                                                                    </Box>
                                                                </InputAdornment>
                                                            ),
                                                            style: {
                                                                borderRadius:
                                                                    '10px'
                                                            }
                                                        }}
                                                    />
                                                </Grid>
                                            </Grid>
                                        </Box>

                                        <Divider />

                                        <Box>
                                            <Typography
                                                variant="subtitle2"
                                                sx={{
                                                    fontWeight: 600,
                                                    color: 'text.secondary',
                                                    mb: 1.5
                                                }}
                                            >
                                                Select/Enter Correct Answer Text
                                            </Typography>
                                            <TextField
                                                select
                                                fullWidth
                                                label="Correct Answer"
                                                name="answer"
                                                value={getSelectedAnswerKey(
                                                    formData
                                                )}
                                                onChange={handleChange}
                                                InputProps={{
                                                    startAdornment: (
                                                        <InputAdornment position="start">
                                                            <Box
                                                                sx={{
                                                                    width: 24,
                                                                    height: 24,
                                                                    borderRadius:
                                                                        '50%',
                                                                    backgroundColor:
                                                                        'rgba(76, 175, 80, 0.1)',
                                                                    color: '#4CAF50',
                                                                    display:
                                                                        'flex',
                                                                    alignItems:
                                                                        'center',
                                                                    justifyContent:
                                                                        'center',
                                                                    fontWeight:
                                                                        'bold',
                                                                    fontSize:
                                                                        '0.85rem'
                                                                }}
                                                            >
                                                                ✓
                                                            </Box>
                                                        </InputAdornment>
                                                    ),
                                                    style: {
                                                        borderRadius: '10px'
                                                    }
                                                }}
                                            >
                                                <MenuItem value="option1">
                                                    Option A:{' '}
                                                    {formData?.options
                                                        ?.option1 || ''}
                                                </MenuItem>
                                                <MenuItem value="option2">
                                                    Option B:{' '}
                                                    {formData?.options
                                                        ?.option2 || ''}
                                                </MenuItem>
                                                <MenuItem value="option3">
                                                    Option C:{' '}
                                                    {formData?.options
                                                        ?.option3 || ''}
                                                </MenuItem>
                                                <MenuItem value="option4">
                                                    Option D:{' '}
                                                    {formData?.options
                                                        ?.option4 || ''}
                                                </MenuItem>
                                            </TextField>
                                        </Box>
                                    </Stack>
                                ) : (
                                    <Box>
                                        <Typography
                                            variant="subtitle2"
                                            sx={{
                                                fontWeight: 600,
                                                color: 'text.secondary',
                                                mb: 2
                                            }}
                                        >
                                            Select Correct Statement Veracity
                                        </Typography>
                                        <FormControl
                                            component="fieldset"
                                            sx={{ width: '100%' }}
                                        >
                                            <RadioGroup
                                                aria-labelledby="true-false-radio-buttons-group-label"
                                                name="answer"
                                                value={formData?.answer}
                                                onChange={handleChange}
                                                sx={{
                                                    flexDirection: 'row',
                                                    gap: 3,
                                                    width: '100%'
                                                }}
                                            >
                                                <FormControlLabel
                                                    value="true"
                                                    control={
                                                        <Radio color="secondary" />
                                                    }
                                                    label={
                                                        <Box sx={{ ml: 1 }}>
                                                            <Typography
                                                                variant="body1"
                                                                sx={{
                                                                    fontWeight: 600
                                                                }}
                                                            >
                                                                True
                                                            </Typography>
                                                            <Typography
                                                                variant="caption"
                                                                color="text.secondary"
                                                            >
                                                                Select if the
                                                                statement is
                                                                correct
                                                            </Typography>
                                                        </Box>
                                                    }
                                                    sx={{
                                                        flex: 1,
                                                        margin: 0,
                                                        p: 2.5,
                                                        border: '2px solid',
                                                        borderColor:
                                                            formData?.answer ===
                                                            'true'
                                                                ? 'secondary.main'
                                                                : theme.palette
                                                                      .divider,
                                                        borderRadius: '12px',
                                                        backgroundColor:
                                                            formData?.answer ===
                                                            'true'
                                                                ? 'rgba(82, 146, 221, 0.03)'
                                                                : 'transparent',
                                                        transition:
                                                            'all 0.2s ease',
                                                        '&:hover': {
                                                            borderColor:
                                                                'secondary.main',
                                                            backgroundColor:
                                                                'rgba(82, 146, 221, 0.01)',
                                                            cursor: 'pointer'
                                                        }
                                                    }}
                                                />
                                                <FormControlLabel
                                                    value="false"
                                                    control={
                                                        <Radio color="secondary" />
                                                    }
                                                    label={
                                                        <Box sx={{ ml: 1 }}>
                                                            <Typography
                                                                variant="body1"
                                                                sx={{
                                                                    fontWeight: 600
                                                                }}
                                                            >
                                                                False
                                                            </Typography>
                                                            <Typography
                                                                variant="caption"
                                                                color="text.secondary"
                                                            >
                                                                Select if the
                                                                statement is
                                                                incorrect
                                                            </Typography>
                                                        </Box>
                                                    }
                                                    sx={{
                                                        flex: 1,
                                                        margin: 0,
                                                        p: 2.5,
                                                        border: '2px solid',
                                                        borderColor:
                                                            formData?.answer ===
                                                            'false'
                                                                ? 'secondary.main'
                                                                : theme.palette
                                                                      .divider,
                                                        borderRadius: '12px',
                                                        backgroundColor:
                                                            formData?.answer ===
                                                            'false'
                                                                ? 'rgba(82, 146, 221, 0.03)'
                                                                : 'transparent',
                                                        transition:
                                                            'all 0.2s ease',
                                                        '&:hover': {
                                                            borderColor:
                                                                'secondary.main',
                                                            backgroundColor:
                                                                'rgba(82, 146, 221, 0.01)',
                                                            cursor: 'pointer'
                                                        }
                                                    }}
                                                />
                                            </RadioGroup>
                                        </FormControl>
                                    </Box>
                                )}

                                <Box
                                    display="flex"
                                    justifyContent="flex-start"
                                    gap={2}
                                    mt={2}
                                >
                                    <Button
                                        color="secondary"
                                        variant="contained"
                                        onClick={handleSubmit}
                                        sx={{
                                            px: 5,
                                            py: 1.2,
                                            fontWeight: 'bold',
                                            borderRadius: '8px',
                                            textTransform: 'none'
                                        }}
                                    >
                                        {shouldEditQuestion
                                            ? 'Save Question'
                                            : 'Submit Question'}
                                    </Button>
                                    <Button
                                        color="inherit"
                                        variant="outlined"
                                        onClick={() => {
                                            dispatch(
                                                setCurrentScreenName('manual')
                                            );
                                            dispatch(
                                                setCurrentScreenState(false)
                                            );
                                            dispatch(setCurrentScreenId(''));
                                        }}
                                        sx={{
                                            px: 4,
                                            py: 1.2,
                                            fontWeight: 'bold',
                                            borderRadius: '8px',
                                            textTransform: 'none'
                                        }}
                                    >
                                        Cancel
                                    </Button>
                                </Box>
                            </Stack>
                        </CardContent>
                    </Card>
                    <ToastContainer />
                </Box>
            )}
        </>
    );
};

export default Exercise;
